import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import API from "../../Api/Api";
import { ENDPOINTS } from "../../Api/Endpoints";
import { sendOtp } from "../../Features/Postes/Thunk/authThunk";
import { signupDoctor } from "../../Features/Postes/Thunk/doctorAuthThunk/doctorAuthThunk";
import { tokenManager } from "../../Api/tokenManager";
import { showAlert } from "../../feedback/feedbackApi";
import { validateEmailInput } from "../../utils/emailValidation";
import patientImage from "../../Assets/pesent.png";
import doctorImage from "../../Assets/doc_sin.png";
import hospitalImage from "../../Assets/doctors.png";
import "./AuthGateway.scss";

const ROLE_OPTIONS = [
  {
    id: "patient",
    title: "Patient",
    subtitle: "Consultation and treatment journey",
    hint: "Use your registered mobile number to receive OTP instantly.",
    image: patientImage,
    imageAlt: "Patient support",
  },
  {
    id: "doctor",
    title: "Doctor",
    subtitle: "Practice and patient management",
    hint: "Login supports OTP. Sign up continues to detailed onboarding.",
    image: doctorImage,
    imageAlt: "Doctor profile",
  },
  {
    id: "hospital",
    title: "Hospital",
    subtitle: "Team, OPD, and care coordination",
    hint: "Login supports OTP. Sign up continues to facility verification.",
    image: hospitalImage,
    imageAlt: "Hospital care team",
  },
];

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const TIME_OPTIONS_30_MIN = Array.from({ length: 48 }, (_, index) => {
  const hour = Math.floor(index / 2);
  const minute = index % 2 === 0 ? "00" : "30";
  return `${String(hour).padStart(2, "0")}:${minute}`;
});

const timeToMinutes = (timeValue) => {
  const [hourText, minuteText] = String(timeValue || "").split(":");
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return NaN;
  return hour * 60 + minute;
};

const normalizeTo30Minute = (timeValue) => {
  const mins = timeToMinutes(timeValue);
  if (!Number.isFinite(mins)) return "09:00";
  const normalized = Math.max(0, Math.min(23 * 60 + 30, Math.round(mins / 30) * 30));
  const hour = Math.floor(normalized / 60);
  const minute = normalized % 60;
  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const isValid30MinuteSlot = (row) => {
  const startMinutes = timeToMinutes(row?.startTime);
  const endMinutes = timeToMinutes(row?.endTime);
  if (!Number.isFinite(startMinutes) || !Number.isFinite(endMinutes)) return false;
  if (startMinutes % 30 !== 0 || endMinutes % 30 !== 0) return false;
  return endMinutes - startMinutes >= 30;
};

const formatDisplayTime = (timeValue) => {
  const [hourText, minuteText] = String(timeValue || "00:00").split(":");
  const hour = Number(hourText);
  const minute = Number(minuteText);
  const safeHour = Number.isFinite(hour) ? hour : 0;
  const safeMinute = Number.isFinite(minute) ? minute : 0;
  const suffix = safeHour >= 12 ? "PM" : "AM";
  const clockHour = safeHour % 12 || 12;
  return `${clockHour}:${String(safeMinute).padStart(2, "0")} ${suffix}`;
};

const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];
const newSlotRow = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  day: "Mon",
  startTime: "09:00",
  endTime: "13:00",
  location: "",
  contactNumber: "",
  usePersonalNumber: false,
});

const createInitialPatientSignup = () => ({
  name: "",
  gender: "",
  dob: "",
  bloodGroup: "",
  imageFile: null,
});

const createInitialProviderForm = () => ({
  fullName: "",
  email: "",
  city: "",
  district: "",
  stateName: "",
  pincode: "",
  experience: "",
  regId: "",
  emergencyMobile: "",
  internalContact: "",
  usePersonalInternalContact: true,
  imageFile: null,
  degreeFile: null,
  regFile: null,
  categoryIds: [],
  subcategoryIds: [],
  slotRows: [],
  /** Doctor medical degrees — multiple titles (e.g. MBBS, MD). */
  degrees: [{ id: `deg-${Date.now()}`, title: "", institution: "", year: "" }],
});

const getProviderPaths = (role) => {
  if (role === "doctor") {
    return {
      loginEndpoint: ENDPOINTS.DOCTOR_LOGIN,
      verifyPath: "/DoctorVerifyOTP",
      roleLabel: "doctor",
    };
  }
  return {
    loginEndpoint: ENDPOINTS.HOSPITAL_LOGIN,
    verifyPath: "/HospitalVerifyOTP",
    roleLabel: "hospital",
  };
};

/** Normalize axios response bodies from `/app/categories` and `/app/subcategories` */
const extractItemsArray = (res) => {
  const data = res?.data;
  if (!data) return [];
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.categories)) return data.categories;
  if (Array.isArray(data.data?.items)) return data.data.items;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data)) return data;
  return [];
};

const normalizeSubcategoryRow = (raw) => {
  const id = String(raw?.subCategoryId ?? raw?._id ?? raw?.id ?? "").trim();
  if (!id) return null;
  return {
    id,
    name: String(raw?.subCategoryName ?? raw?.name ?? "Unnamed"),
    parentSubCategoryId: raw?.parentSubCategoryId ? String(raw.parentSubCategoryId) : null,
    level: Math.min(5, Math.max(2, Number(raw?.level) || 2)),
  };
};

const buildSubcategoryTree = (items) => {
  const normalized = items.map(normalizeSubcategoryRow).filter(Boolean);
  const byId = new Map();
  normalized.forEach((n) => {
    byId.set(n.id, { ...n, children: [] });
  });
  const roots = [];
  byId.forEach((node) => {
    const pid = node.parentSubCategoryId;
    if (pid && byId.has(pid)) {
      byId.get(pid).children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortRec = (n) => {
    n.children.sort((a, b) => a.name.localeCompare(b.name));
    n.children.forEach(sortRec);
  };
  roots.sort((a, b) => a.name.localeCompare(b.name));
  roots.forEach(sortRec);
  return roots;
};

function AuthGatewaySubcategoryTree({ nodes, selectedIds, onToggle }) {
  if (!nodes?.length) return null;
  return (
    <div className="auth_gateway_subtree">
      {nodes.map((node) => (
        <AuthGatewaySubcategoryTreeNode key={node.id} node={node} selectedIds={selectedIds} onToggle={onToggle} depth={0} />
      ))}
    </div>
  );
}

function AuthGatewaySelectCheckbox({
  checked,
  onToggle,
  title,
  label,
  hint = "",
  className = "",
}) {
  return (
    <button
      type="button"
      className={`auth_gateway_checkbox_row ${checked ? "active" : ""} ${className}`.trim()}
      onClick={onToggle}
      title={title || label}
      aria-pressed={checked}
    >
      <span className={`auth_gateway_checkbox_box ${checked ? "checked" : ""}`} aria-hidden="true">
        <span className="auth_gateway_checkbox_tick" />
      </span>
      <span className="auth_gateway_checkbox_copy">
        <span className="auth_gateway_checkbox_label">{label}</span>
        {hint ? <span className="auth_gateway_checkbox_hint">{hint}</span> : null}
      </span>
    </button>
  );
}

function AuthGatewaySubcategoryTreeNode({ node, selectedIds, onToggle, depth }) {
  const selected = selectedIds.some((sid) => String(sid) === String(node.id));
  const hasChildren = node.children?.length > 0;
  const safeLevel = Math.min(5, Math.max(2, Number(node.level) || 2));
  const pickTitle = hasChildren
    ? `${node.name} (${node.children.length} nested under this)`
    : node.name;
  return (
    <div className="auth_gateway_subtree_branch">
      <div
        className={`auth_gateway_subtree_row auth_gateway_subtree_row--level-${safeLevel} ${
          depth > 0 ? "auth_gateway_subtree_row--nested" : ""
        }`.trim()}
        style={{ "--tree-depth": Math.min(depth, 8) }}
      >
        <AuthGatewaySelectCheckbox
          checked={selected}
          onToggle={() => onToggle(node.id)}
          title={pickTitle}
          label={node.name}
          hint={`L${safeLevel}`}
          className={`auth_gateway_checkbox_row--subtree auth_gateway_checkbox_row--level-${safeLevel}`.trim()}
        />
      </div>
      {hasChildren ? (
        <div className="auth_gateway_subtree_children">
          {node.children.map((ch) => (
            <AuthGatewaySubcategoryTreeNode key={ch.id} node={ch} selectedIds={selectedIds} onToggle={onToggle} depth={depth + 1} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function AuthGateway() {
  const brandLogo = "/Assets/logo.png";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { otpLoading } = useSelector((state) => state.auth || {});
  const { signupLoading: providerSignupLoading } = useSelector((state) => state.doctorAuth || {});
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search).get("provider");
      const v = String(p || "").toLowerCase();
      if (v === "doctor" || v === "hospital") return v;
    } catch (_) {}
    return "patient";
  });

  useEffect(() => {
    const p = String(searchParams.get("provider") || "").toLowerCase();
    if (p === "doctor" || p === "hospital") {
      setRole(p);
      const requestedMode = String(searchParams.get("mode") || "").toLowerCase();
      setMode(requestedMode === "signup" ? "signup" : "login");
    }
  }, [searchParams]);
  const mobileKey = useMemo(() => `${mode}-${role}`, [mode, role]);
  const [mobileByContext, setMobileByContext] = useState({});
  const [agreePolicyByContext, setAgreePolicyByContext] = useState({});
  const [busy, setBusy] = useState(false);
  const [inlineError, setInlineError] = useState("");
  const [providerStepByContext, setProviderStepByContext] = useState({});
  const [pincodeLookupLoading, setPincodeLookupLoading] = useState(false);
  const [pincodeLookupMessage, setPincodeLookupMessage] = useState("");
  const [categories, setCategories] = useState([]);
  const [subcategoryMap, setSubcategoryMap] = useState({});
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [patientSignupByContext, setPatientSignupByContext] = useState({});
  const [providerFormByContext, setProviderFormByContext] = useState({});
  const [patientImagePreviewUrl, setPatientImagePreviewUrl] = useState("");
  const [providerImagePreviewUrl, setProviderImagePreviewUrl] = useState("");
  const [providerDegreePreviewUrl, setProviderDegreePreviewUrl] = useState("");
  const [providerRegPreviewUrl, setProviderRegPreviewUrl] = useState("");
  const [isMobileStep4View, setIsMobileStep4View] = useState(false);
  const [mobileSubcatModalCategoryId, setMobileSubcatModalCategoryId] = useState("");

  const isLogin = mode === "login";
  const mobile = mobileByContext[mobileKey] || "";
  const agreePolicy = agreePolicyByContext[mobileKey] ?? false;
  const providerStep = providerStepByContext[mobileKey] || 1;
  const patientSignup = patientSignupByContext[mobileKey] || createInitialPatientSignup();
  const providerForm = providerFormByContext[mobileKey] || createInitialProviderForm();
  const degreeFileName = String(providerForm?.degreeFile?.name || "");
  const regFileName = String(providerForm?.regFile?.name || "");
  const degreeIsPdf =
    Boolean(providerForm?.degreeFile) &&
    (String(providerForm.degreeFile?.type || "").toLowerCase().includes("pdf") ||
      degreeFileName.toLowerCase().endsWith(".pdf"));
  const regIsPdf =
    Boolean(providerForm?.regFile) &&
    (String(providerForm.regFile?.type || "").toLowerCase().includes("pdf") ||
      regFileName.toLowerCase().endsWith(".pdf"));
  const cleanedMobile = useMemo(
    () => String(mobile || "").replace(/\D/g, "").slice(0, 10),
    [mobile]
  );
  const activeRole = useMemo(
    () => ROLE_OPTIONS.find((item) => item.id === role) || ROLE_OPTIONS[0],
    [role]
  );
  const isProviderSignup = !isLogin && role !== "patient";
  /** Steps: 1 mobile, 2 email, 3 location, 4 category/subcategory, 5 profile+docs (+ hospital slots), 6 doctor slots */
  const providerTotalSteps = role === "doctor" ? 6 : 5;
  const isSubmitting = otpLoading || busy || providerSignupLoading;

  const onMobileChange = (event) => {
    const value = event.target.value.replace(/\D/g, "").slice(0, 10);
    setMobileByContext((prev) => ({ ...prev, [mobileKey]: value }));
  };

  const setProviderFormForContext = useCallback((updater) => {
    setProviderFormByContext((prev) => {
      const current = prev[mobileKey] || createInitialProviderForm();
      const nextValue = typeof updater === "function" ? updater(current) : updater;
      return { ...prev, [mobileKey]: nextValue };
    });
  }, [mobileKey]);

  const setPatientSignupForContext = (updater) => {
    setPatientSignupByContext((prev) => {
      const current = prev[mobileKey] || createInitialPatientSignup();
      const nextValue = typeof updater === "function" ? updater(current) : updater;
      return { ...prev, [mobileKey]: nextValue };
    });
  };

  const setProviderStepForContext = (updater) => {
    setProviderStepByContext((prev) => {
      const current = prev[mobileKey] || 1;
      const nextValue = typeof updater === "function" ? updater(current) : updater;
      return { ...prev, [mobileKey]: nextValue };
    });
  };

  const updateProviderField = (key, value) => {
    setProviderFormForContext((prev) => ({ ...prev, [key]: value }));
  };

  const updatePatientSignupField = (key, value) => {
    setPatientSignupForContext((prev) => ({ ...prev, [key]: value }));
  };

  const handlePatientImageChange = (event) => {
    const file = event.target.files?.[0] || null;
    if (!file) {
      updatePatientSignupField("imageFile", null);
      return;
    }
    if (!/^image\/(jpe?g|png|webp)$/i.test(file.type)) {
      setInlineError("Please upload JPG, PNG, or WEBP image.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setInlineError("Image should be 5MB or smaller.");
      return;
    }
    setInlineError("");
    updatePatientSignupField("imageFile", file);
  };

  const subcategoryOptions = useMemo(() => {
    const selected = providerForm.categoryIds;
    return selected.flatMap((categoryId) => subcategoryMap[categoryId] || []);
  }, [providerForm.categoryIds, subcategoryMap]);

  const selectedCategoryNames = useMemo(() => {
    const categoryMap = new Map(categories.map((c) => [String(c._id || c.id), c.name]));
    return providerForm.categoryIds.map((id) => categoryMap.get(String(id))).filter(Boolean);
  }, [categories, providerForm.categoryIds]);

  const categoryTitleById = useMemo(() => {
    const m = new Map();
    categories.forEach((c) => {
      const id = String(c._id || c.id);
      if (id) m.set(id, c.name || id);
    });
    return m;
  }, [categories]);

  const validSlotRows = useMemo(
    () =>
      providerForm.slotRows.filter(
        (row) => row.location.trim().length >= 2 && isValid30MinuteSlot(row)
      ),
    [providerForm.slotRows]
  );

  const ensureValidMobile = () => {
    if (cleanedMobile.length !== 10) {
      setInlineError("Enter a valid 10-digit mobile number.");
      return false;
    }
    return true;
  };

  const submitPatientOtp = async () => {
    const intent = isLogin ? "login" : "signup";
    if (!isLogin) {
      if (patientSignup.name.trim().length < 2) {
        setInlineError("Enter full name");
        return;
      }
      if (!patientSignup.gender) {
        setInlineError("Select gender");
        return;
      }
      if (!patientSignup.dob) {
        setInlineError("Select date of birth");
        return;
      }
      if (!patientSignup.bloodGroup) {
        setInlineError("Select blood group");
        return;
      }
    }
    await dispatch(
      sendOtp({
        mobile: cleanedMobile,
        intent,
      })
    ).unwrap();
    navigate("/verify-otp", {
      state: {
        phone: cleanedMobile,
        intent,
        profileDraft: !isLogin
          ? {
              name: patientSignup.name.trim(),
              gender: patientSignup.gender,
              dob: patientSignup.dob,
              bloodGroup: patientSignup.bloodGroup,
              imageFile: patientSignup.imageFile || null,
            }
          : null,
      },
    });
  };

  const submitProviderLoginOtp = async () => {
    const provider = getProviderPaths(role);
    setBusy(true);
    try {
      tokenManager.setActiveProviderRole(provider.roleLabel);
      await API.post(provider.loginEndpoint, { mobile: cleanedMobile });
      navigate(provider.verifyPath, {
        state: { mobile: cleanedMobile },
      });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    setInlineError("");
  }, [role, mode]);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 900px)");
    const onChange = (event) => setIsMobileStep4View(event.matches);
    setIsMobileStep4View(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (!patientSignup?.imageFile) {
      setPatientImagePreviewUrl("");
      return undefined;
    }
    const preview = URL.createObjectURL(patientSignup.imageFile);
    setPatientImagePreviewUrl(preview);
    return () => URL.revokeObjectURL(preview);
  }, [patientSignup?.imageFile]);

  useEffect(() => {
    if (!providerForm?.imageFile) {
      setProviderImagePreviewUrl("");
      return undefined;
    }
    const preview = URL.createObjectURL(providerForm.imageFile);
    setProviderImagePreviewUrl(preview);
    return () => URL.revokeObjectURL(preview);
  }, [providerForm?.imageFile]);

  useEffect(() => {
    if (!providerForm?.degreeFile) {
      setProviderDegreePreviewUrl("");
      return undefined;
    }
    const isImage = /^image\//i.test(providerForm.degreeFile.type || "");
    if (!isImage) {
      setProviderDegreePreviewUrl("");
      return undefined;
    }
    const preview = URL.createObjectURL(providerForm.degreeFile);
    setProviderDegreePreviewUrl(preview);
    return () => URL.revokeObjectURL(preview);
  }, [providerForm?.degreeFile]);

  useEffect(() => {
    if (!providerForm?.regFile) {
      setProviderRegPreviewUrl("");
      return undefined;
    }
    const isImage = /^image\//i.test(providerForm.regFile.type || "");
    if (!isImage) {
      setProviderRegPreviewUrl("");
      return undefined;
    }
    const preview = URL.createObjectURL(providerForm.regFile);
    setProviderRegPreviewUrl(preview);
    return () => URL.revokeObjectURL(preview);
  }, [providerForm?.regFile]);

  useEffect(() => {
    if (!isProviderSignup) return;
    let cancelled = false;
    async function fetchCategories() {
      if (categories.length > 0) return;
      setCategoriesLoading(true);
      try {
        const res = await API.get(ENDPOINTS.GET_CATEGORIES, { params: { limit: 500, page: 1 } });
        const list = extractItemsArray(res);
        if (!cancelled) setCategories(list);
      } catch {
        if (!cancelled) setCategories([]);
      } finally {
        if (!cancelled) setCategoriesLoading(false);
      }
    }
    fetchCategories();
    return () => {
      cancelled = true;
    };
  }, [isProviderSignup, categories.length]);

  const reloadCategories = async () => {
    setCategoriesLoading(true);
    try {
      const res = await API.get(ENDPOINTS.GET_CATEGORIES, { params: { limit: 500, page: 1 } });
      setCategories(extractItemsArray(res));
    } catch {
      setCategories([]);
    } finally {
      setCategoriesLoading(false);
    }
  };

  useEffect(() => {
    if (!isProviderSignup || providerStep !== 3) return;

    const pin = String(providerForm.pincode || "");
    if (pin.length !== 6) {
      setPincodeLookupLoading(false);
      setPincodeLookupMessage("");
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setPincodeLookupLoading(true);
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
        const result = await response.json();
        const first = Array.isArray(result) ? result[0] : null;
        const offices = Array.isArray(first?.PostOffice) ? first.PostOffice : [];
        const office = offices[0];

        if (!cancelled && office?.State && office?.District) {
          setProviderFormForContext((prev) => ({
            ...prev,
            stateName: office.State,
            district: office.District,
          }));
          setPincodeLookupMessage(`Auto-filled: ${office.District}, ${office.State}`);
          return;
        }

        if (!cancelled) {
          setPincodeLookupMessage("Could not find location for this pincode.");
        }
      } catch {
        if (!cancelled) {
          setPincodeLookupMessage("Unable to fetch location from pincode right now.");
        }
      } finally {
        if (!cancelled) setPincodeLookupLoading(false);
      }
    }, 350);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [isProviderSignup, providerStep, providerForm.pincode, mobileKey, setProviderFormForContext]);

  const fetchSubcategoriesForCategory = useCallback(async (categoryId) => {
    const cid = String(categoryId || "");
    if (!cid) return [];
    if (subcategoryMap[cid]) return subcategoryMap[cid];
    try {
      const res = await API.get(ENDPOINTS.GET_SUBCATEGORIES, {
        params: { categoryId: cid, limit: 500, page: 1 },
      });
      const items = extractItemsArray(res);
      setSubcategoryMap((prev) => ({ ...prev, [cid]: items }));
      return items;
    } catch {
      setSubcategoryMap((prev) => ({ ...prev, [cid]: [] }));
      return [];
    }
  }, [subcategoryMap]);

  const subIdFromRow = (sub) => String(sub?.subCategoryId ?? sub?._id ?? sub?.id ?? sub?.subcategoryId ?? "");

  const toggleCategory = async (categoryId) => {
    const id = String(categoryId);
    const exists = providerForm.categoryIds.includes(id);
    if (exists) {
      const nextCategories = providerForm.categoryIds.filter((c) => c !== id);
      const allowedSubcategoryIds = nextCategories.flatMap((catId) =>
        (subcategoryMap[catId] || []).map((sub) => subIdFromRow(sub)).filter(Boolean)
      );
      setProviderFormForContext((prev) => ({
        ...prev,
        categoryIds: nextCategories,
        subcategoryIds: prev.subcategoryIds.filter((subId) => allowedSubcategoryIds.includes(subId)),
      }));
      return;
    }
    const items = await fetchSubcategoriesForCategory(id);
    const nextCategories = [...providerForm.categoryIds, id];
    const newIds = items.map((sub) => subIdFromRow(sub)).filter(Boolean);
    const allowedSubcategoryIds = nextCategories.flatMap((catId) => {
      if (catId === id) return newIds;
      return (subcategoryMap[catId] || []).map((sub) => subIdFromRow(sub)).filter(Boolean);
    });
    setProviderFormForContext((prev) => ({
      ...prev,
      categoryIds: nextCategories,
      subcategoryIds: prev.subcategoryIds.filter((subId) => allowedSubcategoryIds.includes(subId)),
    }));
  };

  const toggleSubcategory = (subcategoryId) => {
    const id = String(subcategoryId);
    setProviderFormForContext((prev) => ({
      ...prev,
      subcategoryIds: prev.subcategoryIds.includes(id)
        ? prev.subcategoryIds.filter((v) => v !== id)
        : [...prev.subcategoryIds, id],
    }));
  };

  const selectAllCategories = async () => {
    const allCategoryIds = categories.map((cat) => String(cat._id || cat.id)).filter(Boolean);
    await Promise.all(allCategoryIds.map((id) => fetchSubcategoriesForCategory(id)));
    setProviderFormForContext((prev) => ({ ...prev, categoryIds: allCategoryIds }));
  };

  const selectAllSubcategories = () => {
    const allSubcategoryIds = subcategoryOptions
      .map((sub) => subIdFromRow(sub))
      .filter(Boolean);
    setProviderFormForContext((prev) => ({ ...prev, subcategoryIds: allSubcategoryIds }));
  };

  const selectAllSubcategoriesForCategory = async (categoryId) => {
    const cid = String(categoryId || "");
    if (!cid) return;
    const items = await fetchSubcategoriesForCategory(cid);
    const ids = items.map((sub) => subIdFromRow(sub)).filter(Boolean);
    setProviderFormForContext((prev) => ({
      ...prev,
      subcategoryIds: [...new Set([...prev.subcategoryIds, ...ids])],
    }));
  };

  const clearSubcategoriesForCategory = (categoryId) => {
    const cid = String(categoryId || "");
    const ids = (subcategoryMap[cid] || []).map((sub) => subIdFromRow(sub)).filter(Boolean);
    setProviderFormForContext((prev) => ({
      ...prev,
      subcategoryIds: prev.subcategoryIds.filter((sid) => !ids.includes(sid)),
    }));
  };

  const handleCategoryRowPress = async (categoryId) => {
    const id = String(categoryId || "");
    if (!id) return;
    const selected = providerForm.categoryIds.includes(id);
    if (isMobileStep4View && providerStep === 4) {
      if (!selected) {
        await toggleCategory(id);
      } else {
        await fetchSubcategoriesForCategory(id);
      }
      setMobileSubcatModalCategoryId(id);
      return;
    }
    await toggleCategory(id);
  };

  useEffect(() => {
    if (!isProviderSignup || providerStep !== 4) return;
    providerForm.categoryIds.forEach((cid) => {
      void fetchSubcategoriesForCategory(cid);
    });
  }, [isProviderSignup, providerStep, providerForm.categoryIds, fetchSubcategoriesForCategory]);

  useEffect(() => {
    if (!isMobileStep4View && mobileSubcatModalCategoryId) {
      setMobileSubcatModalCategoryId("");
    }
  }, [isMobileStep4View, mobileSubcatModalCategoryId]);

  /** Per specialty: if you tick no procedures, we treat that specialty as covering every listed procedure (all levels). */
  const step4ProceduresStillLoading = useMemo(
    () => providerForm.categoryIds.some((cid) => subcategoryMap[cid] === undefined),
    [providerForm.categoryIds, subcategoryMap]
  );

  const resolvedSubcategoryIds = useMemo(() => {
    const out = [];
    const seen = new Set();
    providerForm.categoryIds.forEach((catId) => {
      const items = subcategoryMap[catId];
      if (items === undefined || items.length === 0) return;
      const allIds = items.map((sub) => subIdFromRow(sub)).filter(Boolean);
      const selectedInCat = providerForm.subcategoryIds.filter((sid) => allIds.includes(sid));
      const useIds = selectedInCat.length > 0 ? selectedInCat : allIds;
      useIds.forEach((id) => {
        if (!seen.has(id)) {
          seen.add(id);
          out.push(id);
        }
      });
    });
    return out;
  }, [providerForm.categoryIds, providerForm.subcategoryIds, subcategoryMap]);

  const resolvedSubcategoryNames = useMemo(() => {
    const map = new Map(
      subcategoryOptions.map((s) => {
        const id = String(s._id || s.id || s.subcategoryId || s.subCategoryId);
        const name = s.name || s.subCategoryName || s.subcategoryName;
        return [id, name];
      })
    );
    return resolvedSubcategoryIds.map((id) => map.get(String(id))).filter(Boolean);
  }, [subcategoryOptions, resolvedSubcategoryIds]);

  const providerSelectionPayload = useMemo(() => {
    const selectedCategoryIds = providerForm.categoryIds.map((id) => String(id)).filter(Boolean);
    const selectedSubCategoryIds = resolvedSubcategoryIds.map((id) => String(id)).filter(Boolean);

    const categorySubcategoryTree = selectedCategoryIds.map((catId) => {
      const rows = Array.isArray(subcategoryMap[catId]) ? subcategoryMap[catId] : [];
      const allIdsForCategory = rows.map((row) => subIdFromRow(row)).filter(Boolean);
      const selectedInCategory = providerForm.subcategoryIds
        .map((id) => String(id))
        .filter((sid) => allIdsForCategory.includes(sid));
      const effectiveSubcategoryIds = selectedInCategory.length > 0 ? selectedInCategory : allIdsForCategory;
      const selectedSet = new Set(effectiveSubcategoryIds);

      const subcategories = rows
        .map((row) => {
          const subId = subIdFromRow(row);
          if (!subId || !selectedSet.has(subId)) return null;
          return {
            subCategoryId: subId,
            name: String(row?.subCategoryName || row?.subcategoryName || row?.name || ""),
            parentSubCategoryId: row?.parentSubCategoryId
              ? String(row.parentSubCategoryId)
              : null,
            level: Math.min(5, Math.max(2, Number(row?.level) || 2)),
          };
        })
        .filter(Boolean);

      return {
        categoryId: catId,
        categoryName: categoryTitleById.get(catId) || catId,
        selectedSubCategoryIds: effectiveSubcategoryIds,
        subcategories,
      };
    });

    const selectedSelections = categorySubcategoryTree.map((entry) => ({
      categoryId: entry.categoryId,
      subCategoryIds: entry.selectedSubCategoryIds,
    }));

    return {
      selectedCategoryIds,
      selectedSubCategoryIds,
      selectedSelections,
      categorySubcategoryTree,
    };
  }, [
    providerForm.categoryIds,
    providerForm.subcategoryIds,
    resolvedSubcategoryIds,
    subcategoryMap,
    categoryTitleById,
  ]);

  const mobileModalItems = useMemo(() => {
    if (!mobileSubcatModalCategoryId) return [];
    return subcategoryMap[mobileSubcatModalCategoryId] || [];
  }, [mobileSubcatModalCategoryId, subcategoryMap]);

  const mobileModalSelectedCount = useMemo(() => {
    if (!mobileSubcatModalCategoryId || mobileModalItems.length === 0) return 0;
    const modalIds = mobileModalItems.map((row) => subIdFromRow(row)).filter(Boolean);
    return providerForm.subcategoryIds.filter((sid) => modalIds.includes(sid)).length;
  }, [mobileSubcatModalCategoryId, mobileModalItems, providerForm.subcategoryIds]);

  const updateSlotRow = (id, patch) => {
    setProviderFormForContext((prev) => ({
      ...prev,
      slotRows: prev.slotRows.map((row) => {
        if (row.id !== id) return row;
        return { ...row, ...patch };
      }),
    }));
  };

  const saveSlotRow = (id) => {
    const row = providerForm.slotRows.find((item) => item.id === id);
    if (!row) return;
    const isRowValid = row.location.trim().length >= 2 && isValid30MinuteSlot(row);
    if (!isRowValid) {
      setInlineError("Fill valid slot details before saving.");
      return;
    }
    setInlineError("");
  };

  const addSlotRow = () => {
    setProviderFormForContext((prev) => ({ ...prev, slotRows: [...prev.slotRows, newSlotRow()] }));
  };

  const removeSlotRow = (id) => {
    setProviderFormForContext((prev) => ({
      ...prev,
      slotRows: prev.slotRows.length <= 1 ? prev.slotRows : prev.slotRows.filter((row) => row.id !== id),
    }));
  };

  const addDegreeRow = () => {
    setProviderFormForContext((prev) => {
      if ((prev.degrees || []).length >= 10) return prev;
      return {
        ...prev,
        degrees: [
          ...(prev.degrees || []),
          { id: `deg-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, title: "", institution: "", year: "" },
        ],
      };
    });
  };

  const updateDegreeRow = (id, patch) => {
    setProviderFormForContext((prev) => ({
      ...prev,
      degrees: (prev.degrees || []).map((row) => (row.id === id ? { ...row, ...patch } : row)),
    }));
  };

  const removeDegreeRow = (id) => {
    setProviderFormForContext((prev) => {
      const list = prev.degrees || [];
      if (list.length <= 1) return prev;
      return { ...prev, degrees: list.filter((row) => row.id !== id) };
    });
  };

  const validateProviderStep = (step) => {
    const {
      fullName,
      email,
      district,
      stateName,
      pincode,
      experience,
      regId,
      emergencyMobile,
      imageFile,
      degreeFile,
      regFile,
      categoryIds,
    } = providerForm;

    if (step === 1) {
      if (cleanedMobile.length !== 10) return "Enter a valid 10-digit mobile number.";
    }
    if (step === 2) {
      const emailCheck = validateEmailInput(email);
      if (!emailCheck.valid) return emailCheck.message;
    }
    if (step === 3) {
      if (fullName.trim().length < 2) return role === "hospital" ? "Enter hospital name" : "Enter full name";
      if (district.trim().length < 2) return "Enter district";
      if (stateName.trim().length < 2) return "Enter state";
      if (pincode.length !== 6) return "Enter valid 6-digit pincode";
    }
    if (step === 4) {
      if (categoryIds.length === 0) return "Select at least one category";
      if (step4ProceduresStillLoading) return "Loading procedures for your specialties…";
      if (resolvedSubcategoryIds.length === 0) {
        return "Your chosen specialties have no procedures available yet. Pick another specialty or contact support.";
      }
    }
    if (step === 5) {
      if (!experience.trim()) return role === "hospital" ? "Enter established year" : "Enter years of experience";
      if (regId.trim().length < 4) return "Enter registration/license number";
      if (role === "hospital" && emergencyMobile.length !== 10) return "Enter valid emergency number";
      if (role === "doctor") {
        const titles = (providerForm.degrees || [])
          .map((d) => String(d?.title || "").trim())
          .filter(Boolean);
        if (titles.length === 0) return "Add at least one medical degree (e.g. MBBS, MD)";
      }
      if (!imageFile || !degreeFile || !regFile) return "Upload required documents";
    }
    if (step === 6 && role === "doctor") return "";
    return "";
  };

  const submitProviderSignup = async () => {
    const providerValidationMessage = validateProviderStep(providerTotalSteps);
    if (providerValidationMessage) {
      setInlineError(providerValidationMessage);
      return;
    }

    const specializationLabel = resolvedSubcategoryNames.length
      ? resolvedSubcategoryNames.join(", ")
      : selectedCategoryNames.join(", ");
    const doctorSlotBlocks = providerForm.slotRows
      .filter((row) => row.location.trim() && isValid30MinuteSlot(row))
      .map((row) => ({
        day: row.day,
        startTime: row.startTime,
        endTime: row.endTime,
        location: row.location.trim(),
      }));
    const hospitalSchedules = providerForm.slotRows
      .filter((row) => row.location.trim() && isValid30MinuteSlot(row))
      .map((row) => ({
        departmentName: "General OPD",
        weekdays: [row.day],
        startTime: row.startTime,
        endTime: row.endTime,
        slotDurationMinutes: 30,
        notes: row.location.trim(),
      }));
    const internalContact = providerForm.usePersonalInternalContact
      ? cleanedMobile
      : providerForm.internalContact;

    await dispatch(
      signupDoctor({
        fullName: providerForm.fullName.trim(),
        mobile: cleanedMobile,
        emergencyContactNumber: providerForm.emergencyMobile,
        city: providerForm.city.trim() || providerForm.district.trim(),
        state: providerForm.stateName.trim(),
        specialization: specializationLabel,
        experience: providerForm.experience.trim(),
        regId: providerForm.regId.trim(),
        hospitalType: specializationLabel,
        establishedYear: providerForm.experience.trim(),
        licenseNumber: providerForm.regId.trim(),
        email: providerForm.email.trim(),
        pincode: providerForm.pincode,
        imageFile: providerForm.imageFile,
        degreeFile: providerForm.degreeFile,
        regFile: providerForm.regFile,
        degrees:
          role === "doctor"
            ? (providerForm.degrees || [])
                .map((d) => ({
                  title: String(d?.title || "").trim(),
                  institution: String(d?.institution || "").trim(),
                  year: String(d?.year || "").trim(),
                }))
                .filter((d) => d.title)
            : undefined,
        mode: "inClinic",
        preferredText: `Internal contact: ${internalContact || "N/A"} (not shown to patients)`,
        doctorSlotBlocks,
        emergency24x7: false,
        departmentOpdSchedules: hospitalSchedules,
        isHospitalFlow: role === "hospital",
        selectedCategoryIds: providerSelectionPayload.selectedCategoryIds,
        selectedSubCategoryIds: providerSelectionPayload.selectedSubCategoryIds,
        selectedSelections: providerSelectionPayload.selectedSelections,
        categorySubcategoryTree: providerSelectionPayload.categorySubcategoryTree,
      })
    ).unwrap();

    navigate(role === "hospital" ? "/HospitalVerifyOTP" : "/DoctorVerifyOTP", {
      state: { mobile: cleanedMobile, email: providerForm.email.trim() },
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setInlineError("");

    if (!isProviderSignup && !ensureValidMobile()) return;

    if (!isLogin && role === "patient" && !agreePolicy) {
      setInlineError("Please accept Terms and Privacy Policy to continue.");
      return;
    }

    try {
      if (role === "patient") {
        await submitPatientOtp();
        return;
      }

      if (isLogin) {
        await submitProviderLoginOtp();
        return;
      }

      if (providerStep < providerTotalSteps) {
        const stepError = validateProviderStep(providerStep);
        if (stepError) {
          setInlineError(stepError);
          return;
        }
        setProviderStepForContext((prev) => prev + 1);
        return;
      }

      await submitProviderSignup();
      return;
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        (typeof error === "string" ? error : "") ||
        "Could not continue. Please try again.";
      setInlineError(message);
      showAlert(message, { variant: "error", title: "Action failed" });
    }
  };

  const buttonLabel = (() => {
    if (role === "patient") {
      return isSubmitting
        ? "Sending OTP..."
        : isLogin
          ? "Send OTP and login"
          : "Send OTP and sign up";
    }
    if (isLogin) {
      return isSubmitting ? "Sending OTP..." : "Send OTP and continue";
    }
    if (providerStep < providerTotalSteps) return "Continue";
    return role === "doctor" ? "Create doctor account" : "Create hospital account";
  })();

  const goPreviousStep = () => {
    setInlineError("");
    setProviderStepForContext((prev) => Math.max(1, prev - 1));
  };

  return (
    <div className="auth_gateway_page">
      <div className="auth_gateway_card">
        <div className="auth_gateway_left_section">
          <img src={brandLogo} alt="HealBid" className="auth_gateway_logo" />
          <h1>Login / Sign up</h1>
          <div className="auth_gateway_preview">
            <p className="auth_gateway_preview_kicker">{activeRole.title} flow</p>
            <img src={activeRole.image} alt={activeRole.imageAlt} className="auth_gateway_preview_img" />
          </div>

          <div className="auth_gateway_mode_switch" role="tablist" aria-label="Choose login or sign up">
            <button
              type="button"
              role="tab"
              aria-selected={isLogin}
              className={`auth_gateway_mode_btn ${isLogin ? "active" : ""}`}
              onClick={() => setMode("login")}
            >
              Login
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={!isLogin}
              className={`auth_gateway_mode_btn ${!isLogin ? "active" : ""}`}
              onClick={() => setMode("signup")}
            >
              Sign up
            </button>
          </div>

          <div className="auth_gateway_roles" role="tablist" aria-label="Choose account role">
            {ROLE_OPTIONS.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                className={`auth_gateway_role ${role === item.id ? "active" : ""}`}
                aria-selected={role === item.id}
                onClick={() => {
                  setRole(item.id);
                  setInlineError("");
                }}
              >
                <p className="auth_gateway_role_title">{item.title}</p>
              </button>
            ))}
          </div>
        </div>

        <div className="auth_gateway_right_section">
          <form className="auth_gateway_form" onSubmit={handleSubmit}>
          <p className="auth_gateway_hint_text">{activeRole.hint}</p>
          {isProviderSignup ? (
            <div className="auth_gateway_stepper">
              {Array.from({ length: providerTotalSteps }, (_, idx) => idx + 1).map((step) => (
                <button
                  key={step}
                  type="button"
                  className={`auth_gateway_stepdot ${providerStep === step ? "active" : ""}`}
                  onClick={() => setProviderStepForContext(step)}
                >
                  {step}
                </button>
              ))}
            </div>
          ) : null}

          <div className="auth_gateway_field_wrap">
            <label htmlFor="gateway-mobile">Mobile number</label>
            <div className="auth_gateway_mobile_input">
              <span>+91</span>
              <input
                id="gateway-mobile"
                value={mobile}
                onChange={onMobileChange}
                placeholder="Enter 10-digit mobile number"
                inputMode="numeric"
                autoComplete="tel-national"
                maxLength={10}
                disabled={isSubmitting || (isProviderSignup && providerStep > 1)}
              />
            </div>
          </div>

          {isProviderSignup ? (
            <div className="auth_gateway_provider_fields">
              {providerStep === 2 ? (
                <div className="auth_gateway_field_wrap">
                  <label htmlFor="provider-email">Email</label>
                  <input
                    id="provider-email"
                    className="auth_gateway_text_input"
                    value={providerForm.email}
                    onChange={(e) => updateProviderField("email", e.target.value)}
                    placeholder="Enter email"
                  />
                </div>
              ) : null}

              {providerStep === 3 ? (
                <>
                  <div className="auth_gateway_field_wrap">
                    <label htmlFor="provider-name">{role === "hospital" ? "Hospital name" : "Full name"}</label>
                    <input
                      id="provider-name"
                      className="auth_gateway_text_input"
                      value={providerForm.fullName}
                      onChange={(e) => updateProviderField("fullName", e.target.value)}
                      placeholder={role === "hospital" ? "Enter hospital name" : "Enter full name"}
                    />
                  </div>
                  <div className="auth_gateway_field_row">
                    <div className="auth_gateway_field_wrap">
                      <label htmlFor="provider-district">District</label>
                      <input
                        id="provider-district"
                        className="auth_gateway_text_input"
                        value={providerForm.district}
                        onChange={(e) => updateProviderField("district", e.target.value)}
                        placeholder="District"
                      />
                    </div>
                    <div className="auth_gateway_field_wrap">
                      <label htmlFor="provider-state">State</label>
                      <select
                        id="provider-state"
                        className="auth_gateway_text_input"
                        value={providerForm.stateName}
                        onChange={(e) => updateProviderField("stateName", e.target.value)}
                      >
                        <option value="">Select state</option>
                        {INDIAN_STATES.map((state) => (
                          <option key={state} value={state}>
                            {state}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="auth_gateway_field_wrap">
                    <label htmlFor="provider-city">City / Town (optional)</label>
                    <input
                      id="provider-city"
                      className="auth_gateway_text_input"
                      value={providerForm.city}
                      onChange={(e) => updateProviderField("city", e.target.value)}
                      placeholder="City / Town"
                    />
                  </div>
                  <div className="auth_gateway_field_row">
                    <div className="auth_gateway_field_wrap">
                      <label htmlFor="provider-pincode">Pincode</label>
                      <input
                        id="provider-pincode"
                        className="auth_gateway_text_input"
                        value={providerForm.pincode}
                        onChange={(e) =>
                          updateProviderField("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))
                        }
                        placeholder="6-digit pincode"
                        inputMode="numeric"
                      />
                      {pincodeLookupLoading ? (
                        <small className="auth_gateway_chip_hint">Finding district and state...</small>
                      ) : pincodeLookupMessage ? (
                        <small className="auth_gateway_chip_hint">{pincodeLookupMessage}</small>
                      ) : null}
                    </div>
                    <div className="auth_gateway_field_wrap">
                      <label htmlFor="provider-internal-contact">Internal contact number</label>
                      <input
                        id="provider-internal-contact"
                        className="auth_gateway_text_input"
                        value={
                          providerForm.usePersonalInternalContact
                            ? cleanedMobile
                            : providerForm.internalContact
                        }
                        onChange={(e) =>
                          updateProviderField("internalContact", e.target.value.replace(/\D/g, "").slice(0, 10))
                        }
                        placeholder="Internal number"
                        inputMode="numeric"
                        disabled={providerForm.usePersonalInternalContact}
                      />
                    </div>
                  </div>
                  <label className="auth_gateway_policy">
                    <input
                      type="checkbox"
                      checked={providerForm.usePersonalInternalContact}
                      onChange={(e) => updateProviderField("usePersonalInternalContact", e.target.checked)}
                    />
                    <span>Use personal number as internal contact (not shown to patients).</span>
                  </label>
                </>
              ) : null}

              {providerStep === 4 ? (
                <>
                  <div className="auth_gateway_step4_grid">
                    <div className="auth_gateway_field_wrap auth_gateway_step4_categories">
                      <div className="auth_gateway_picker_header">
                        <label>Category</label>
                        <button type="button" onClick={selectAllCategories} className="auth_gateway_link_btn">
                          Select all
                        </button>
                      </div>
                      <div className="auth_gateway_chip_grid auth_gateway_chip_grid--scroll auth_gateway_chip_grid--categories">
                        {categoriesLoading ? (
                          <span className="auth_gateway_chip_hint">Loading categories...</span>
                        ) : categories.length === 0 ? (
                          <div className="auth_gateway_empty_catalog">
                            <span className="auth_gateway_chip_hint">No categories loaded. Check your connection or try again.</span>
                            <button type="button" className="auth_gateway_retry_btn" onClick={reloadCategories}>
                              Retry
                            </button>
                          </div>
                        ) : (
                          categories.map((category) => {
                            const id = String(category._id || category.id);
                            const selected = providerForm.categoryIds.includes(id);
                            return (
                              <AuthGatewaySelectCheckbox
                                key={id}
                                checked={selected}
                                onToggle={() => handleCategoryRowPress(id)}
                                label={category.name}
                                className="auth_gateway_checkbox_row--category"
                              />
                            );
                          })
                        )}
                      </div>
                    </div>

                    <div className="auth_gateway_field_wrap auth_gateway_step4_subcats">
                      {isMobileStep4View ? (
                        <div className="auth_gateway_subcat_placeholder">
                          <span className="auth_gateway_chip_hint">
                            Tap a category to view operations.
                          </span>
                        </div>
                      ) : (
                        <>
                          <div className="auth_gateway_picker_header">
                            <label>Operations</label>
                            {providerForm.categoryIds.length > 0 && subcategoryOptions.length > 0 ? (
                              <button type="button" onClick={selectAllSubcategories} className="auth_gateway_link_btn">
                                Select all
                              </button>
                            ) : null}
                          </div>

                          {providerForm.categoryIds.length === 0 ? (
                            <div className="auth_gateway_subcat_placeholder">
                              <span className="auth_gateway_chip_hint">Select a category to see operations.</span>
                            </div>
                          ) : (
                            <div className="auth_gateway_subcat_panels">
                              {providerForm.categoryIds.map((catId) => {
                                const items = subcategoryMap[catId];
                                const title = categoryTitleById.get(catId) || catId;
                                const pickedHere =
                                  items && items.length > 0
                                    ? providerForm.subcategoryIds.filter((sid) =>
                                        items.some((row) => subIdFromRow(row) === sid)
                                      )
                                    : [];
                                const fullCategoryCoverage = items && items.length > 0 && pickedHere.length === 0;
                                return (
                                  <div key={catId} className="auth_gateway_subcat_panel">
                                    <div className="auth_gateway_subcat_panel_head">
                                      <span className="auth_gateway_subcat_panel_title">{title}</span>
                                      <span className="auth_gateway_subcat_panel_hint">
                                        {items === undefined
                                          ? "Loading…"
                                          : `${items.length} procedure${items.length === 1 ? "" : "s"}`}
                                      </span>
                                    </div>
                                    {fullCategoryCoverage ? (
                                      <p className="auth_gateway_subcat_implicit">
                                        All operations included for this category.
                                      </p>
                                    ) : null}
                                    {items === undefined ? (
                                      <span className="auth_gateway_chip_hint">Loading operations…</span>
                                    ) : items.length === 0 ? (
                                      <span className="auth_gateway_chip_hint">No operations listed yet.</span>
                                    ) : (
                                      <AuthGatewaySubcategoryTree
                                        nodes={buildSubcategoryTree(items)}
                                        selectedIds={providerForm.subcategoryIds}
                                        onToggle={(sid) => toggleSubcategory(sid)}
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </>
              ) : null}

              {providerStep === 5 ? (
                <>
                  <div className="auth_gateway_field_row">
                    <div className="auth_gateway_field_wrap">
                      <label htmlFor="provider-experience">
                        {role === "hospital" ? "Established year" : "Experience (years)"}
                      </label>
                      <input
                        id="provider-experience"
                        className="auth_gateway_text_input"
                        value={providerForm.experience}
                        onChange={(e) => updateProviderField("experience", e.target.value.replace(/\D/g, "").slice(0, 4))}
                        placeholder={role === "hospital" ? "e.g. 2015" : "Years"}
                        inputMode="numeric"
                      />
                    </div>
                    <div className="auth_gateway_field_wrap">
                      <label htmlFor="provider-regid">Registration / License</label>
                      <input
                        id="provider-regid"
                        className="auth_gateway_text_input"
                        value={providerForm.regId}
                        onChange={(e) => updateProviderField("regId", e.target.value)}
                        placeholder="Enter ID"
                      />
                    </div>
                  </div>

                  {role === "doctor" ? (
                    <div className="auth_gateway_field_wrap">
                      <div className="auth_gateway_picker_header">
                        <label>Medical degrees</label>
                        <button type="button" onClick={addDegreeRow} className="auth_gateway_link_btn">
                          Add degree
                        </button>
                      </div>
                      <p className="auth_gateway_chip_hint" style={{ marginBottom: 8 }}>
                        Add each qualification separately (e.g. MBBS, MD, MS). Upload one supporting degree document below.
                      </p>
                      <div className="auth_gateway_slots">
                        {(providerForm.degrees || []).map((row) => (
                          <div key={row.id} className="auth_gateway_slot_row">
                            <input
                              className="auth_gateway_text_input"
                              value={row.title}
                              onChange={(e) => updateDegreeRow(row.id, { title: e.target.value })}
                              placeholder="Degree title (e.g. MBBS)"
                            />
                            <input
                              className="auth_gateway_text_input"
                              value={row.institution}
                              onChange={(e) => updateDegreeRow(row.id, { institution: e.target.value })}
                              placeholder="Institution (optional)"
                            />
                            <input
                              className="auth_gateway_text_input"
                              value={row.year}
                              onChange={(e) =>
                                updateDegreeRow(row.id, { year: e.target.value.replace(/\D/g, "").slice(0, 4) })
                              }
                              placeholder="Year"
                              inputMode="numeric"
                            />
                            <button
                              type="button"
                              className="auth_gateway_remove_slot"
                              onClick={() => removeDegreeRow(row.id)}
                              disabled={(providerForm.degrees || []).length <= 1}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {role === "doctor" ? null : (
                    <div className="auth_gateway_field_wrap">
                      <label htmlFor="provider-emergency">Emergency contact</label>
                      <input
                        id="provider-emergency"
                        className="auth_gateway_text_input"
                        value={providerForm.emergencyMobile}
                        onChange={(e) =>
                          updateProviderField("emergencyMobile", e.target.value.replace(/\D/g, "").slice(0, 10))
                        }
                        placeholder="10-digit emergency mobile"
                        inputMode="numeric"
                      />
                    </div>
                  )}

                  {role === "hospital" ? (
                    <div className="auth_gateway_field_wrap">
                      <div className="auth_gateway_picker_header">
                        <label>Day-wise availability</label>
                        <button type="button" onClick={addSlotRow} className="auth_gateway_link_btn">
                          Add slot
                        </button>
                      </div>
                      <div className="auth_gateway_slots">
                        {providerForm.slotRows.map((row) => (
                          <div key={row.id} className="auth_gateway_slot_row">
                            <select
                              value={row.day}
                              className="auth_gateway_text_input"
                              onChange={(e) => updateSlotRow(row.id, { day: e.target.value })}
                            >
                              {DAYS.map((day) => (
                                <option key={day} value={day}>
                                  {day}
                                </option>
                              ))}
                            </select>
                            <select
                              value={normalizeTo30Minute(row.startTime)}
                              className="auth_gateway_text_input"
                              onChange={(e) => updateSlotRow(row.id, { startTime: normalizeTo30Minute(e.target.value) })}
                            >
                              {TIME_OPTIONS_30_MIN.map((time) => (
                                <option key={time} value={time}>
                                  {formatDisplayTime(time)}
                                </option>
                              ))}
                            </select>
                            <select
                              value={normalizeTo30Minute(row.endTime)}
                              className="auth_gateway_text_input"
                              onChange={(e) => updateSlotRow(row.id, { endTime: normalizeTo30Minute(e.target.value) })}
                            >
                              {TIME_OPTIONS_30_MIN.map((time) => (
                                <option key={time} value={time}>
                                  {formatDisplayTime(time)}
                                </option>
                              ))}
                            </select>
                            <input
                              className="auth_gateway_text_input"
                              value={row.location}
                              onChange={(e) => updateSlotRow(row.id, { location: e.target.value })}
                              placeholder="Clinic/hospital location"
                            />
                            <button
                              type="button"
                              className="auth_gateway_save_slot"
                              onClick={() => saveSlotRow(row.id)}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="auth_gateway_remove_slot"
                              onClick={() => removeSlotRow(row.id)}
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  <div className="auth_gateway_file_row">
                    <label className="auth_gateway_file_field">
                      <span>Profile photo</span>
                      <div className="auth_gateway_image_uploader">
                        <div className="auth_gateway_image_uploader_preview">
                          {providerImagePreviewUrl ? (
                            <img src={providerImagePreviewUrl} alt="Provider profile preview" />
                          ) : (
                            <span className="auth_gateway_image_uploader_placeholder">No photo selected</span>
                          )}
                        </div>
                        <div className="auth_gateway_image_uploader_actions">
                          <input
                            id="provider-profile-image"
                            type="file"
                            accept=".png,.jpg,.jpeg,.webp"
                            onChange={(e) => updateProviderField("imageFile", e.target.files?.[0] || null)}
                          />
                          <label
                            htmlFor="provider-profile-image"
                            className="auth_gateway_image_uploader_btn"
                          >
                            {providerForm.imageFile ? "Change photo" : "Choose photo"}
                          </label>
                          <p className="auth_gateway_image_uploader_note">
                            {providerForm.imageFile
                              ? providerForm.imageFile.name
                              : "JPG, PNG, WEBP | up to 5MB"}
                          </p>
                        </div>
                      </div>
                    </label>
                    <label className="auth_gateway_file_field">
                      <span>{role === "hospital" ? "Registration document" : "Medical degree"}</span>
                      <div className="auth_gateway_doc_uploader">
                        <div className="auth_gateway_doc_uploader_preview">
                          {providerDegreePreviewUrl ? (
                            <img src={providerDegreePreviewUrl} alt="Medical degree preview" />
                          ) : degreeIsPdf ? (
                            <span className="auth_gateway_doc_uploader_badge">PDF</span>
                          ) : (
                            <span className="auth_gateway_doc_uploader_empty">No preview</span>
                          )}
                        </div>
                        <input
                          id="provider-degree-file"
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={(e) => updateProviderField("degreeFile", e.target.files?.[0] || null)}
                        />
                        <label htmlFor="provider-degree-file" className="auth_gateway_doc_uploader_btn">
                          {providerForm.degreeFile ? "Change file" : "Choose file"}
                        </label>
                        <p className="auth_gateway_doc_uploader_name">
                          {providerForm.degreeFile ? providerForm.degreeFile.name : "PDF, JPG, PNG"}
                        </p>
                      </div>
                    </label>
                    <label className="auth_gateway_file_field">
                      <span>{role === "hospital" ? "Compliance document" : "Registration certificate"}</span>
                      <div className="auth_gateway_doc_uploader">
                        <div className="auth_gateway_doc_uploader_preview">
                          {providerRegPreviewUrl ? (
                            <img src={providerRegPreviewUrl} alt="Registration certificate preview" />
                          ) : regIsPdf ? (
                            <span className="auth_gateway_doc_uploader_badge">PDF</span>
                          ) : (
                            <span className="auth_gateway_doc_uploader_empty">No preview</span>
                          )}
                        </div>
                        <input
                          id="provider-reg-file"
                          type="file"
                          accept=".jpg,.jpeg,.png,.pdf"
                          onChange={(e) => updateProviderField("regFile", e.target.files?.[0] || null)}
                        />
                        <label htmlFor="provider-reg-file" className="auth_gateway_doc_uploader_btn">
                          {providerForm.regFile ? "Change file" : "Choose file"}
                        </label>
                        <p className="auth_gateway_doc_uploader_name">
                          {providerForm.regFile ? providerForm.regFile.name : "PDF, JPG, PNG"}
                        </p>
                      </div>
                    </label>
                  </div>
                </>
              ) : null}

              {providerStep === 6 && role === "doctor" ? (
                <>
                  <div className="auth_gateway_field_wrap">
                    <div className="auth_gateway_picker_header">
                      <label>Doctor slot availability (30-minute intervals)</label>
                      <button type="button" onClick={addSlotRow} className="auth_gateway_link_btn">
                        Add slot
                      </button>
                    </div>
                    <div className="auth_gateway_slots">
                      {providerForm.slotRows.map((row) => {
                        const canSave = row.location.trim().length >= 2 && isValid30MinuteSlot(row);
                        const isSaved = canSave;
                        return (
                          <div
                            key={row.id}
                            className={`auth_gateway_slot_row auth_gateway_slot_row--doctor ${
                              isSaved ? "is-valid" : ""
                            }`}
                          >
                            <div className="auth_gateway_slot_header">
                              <span className="auth_gateway_slot_tag">Slot</span>
                              {isSaved ? (
                                <span className="auth_gateway_slot_tag">Added</span>
                              ) : (
                                <span className="auth_gateway_slot_tag auth_gateway_slot_tag--muted">
                                  Add slot
                                </span>
                              )}
                            </div>
                            <div className="auth_gateway_chip_grid">
                              {DAYS.map((day) => (
                                <button
                                  key={`${row.id}-${day}`}
                                  type="button"
                                  className={`auth_gateway_chip ${row.day === day ? "active" : ""}`}
                                  onClick={() => updateSlotRow(row.id, { day })}
                                >
                                  {day}
                                </button>
                              ))}
                            </div>
                            <div className="auth_gateway_slot_fields">
                              <select
                                value={normalizeTo30Minute(row.startTime)}
                                className="auth_gateway_text_input"
                                onChange={(e) =>
                                  updateSlotRow(row.id, { startTime: normalizeTo30Minute(e.target.value) })
                                }
                              >
                                {TIME_OPTIONS_30_MIN.map((time) => (
                                  <option key={time} value={time}>
                                    {formatDisplayTime(time)}
                                  </option>
                                ))}
                              </select>
                              <select
                                className="auth_gateway_text_input"
                                value={normalizeTo30Minute(row.endTime)}
                                onChange={(e) =>
                                  updateSlotRow(row.id, { endTime: normalizeTo30Minute(e.target.value) })
                                }
                              >
                                {TIME_OPTIONS_30_MIN.map((time) => (
                                  <option key={time} value={time}>
                                    {formatDisplayTime(time)}
                                  </option>
                                ))}
                              </select>
                              <input
                                className="auth_gateway_text_input"
                                value={row.location}
                                onChange={(e) => updateSlotRow(row.id, { location: e.target.value })}
                                placeholder="Clinic / hospital location details"
                              />
                            </div>
                            <div className="auth_gateway_slot_summary">
                              <span className="auth_gateway_slot_pill">
                                {row.day} {formatDisplayTime(row.startTime)} - {formatDisplayTime(row.endTime)}
                              </span>
                              <span className="auth_gateway_slot_pill">
                                {row.location.trim() || "Add clinic/location details"}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="auth_gateway_save_slot"
                              onClick={() => saveSlotRow(row.id)}
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              className="auth_gateway_remove_slot"
                              onClick={() => removeSlotRow(row.id)}
                            >
                              Remove
                            </button>
                          </div>
                        );
                      })}
                    </div>
                    <div className="auth_gateway_slot_summary">
                      {validSlotRows.length > 0 ? (
                        validSlotRows.map((row) => (
                          <span key={`summary-${row.id}`} className="auth_gateway_slot_pill">
                            {row.day} {formatDisplayTime(row.startTime)} - {formatDisplayTime(row.endTime)} |{" "}
                            {row.location.trim()}
                          </span>
                        ))
                      ) : (
                        <span className="auth_gateway_chip_hint">
                          Slots are optional. You can skip now and manage full availability later from Doctor Panel Settings.
                        </span>
                      )}
                    </div>
                  </div>
                </>
              ) : null}
            </div>
          ) : null}

          {!isLogin && role === "patient" ? (
            <>
              <div className="auth_gateway_provider_fields">
                <div className="auth_gateway_field_wrap">
                  <label htmlFor="patient-signup-name">Full name</label>
                  <input
                    id="patient-signup-name"
                    className="auth_gateway_text_input"
                    value={patientSignup.name}
                    onChange={(e) => updatePatientSignupField("name", e.target.value)}
                    placeholder="Enter full name"
                  />
                </div>
                <div className="auth_gateway_field_wrap">
                  <label htmlFor="patient-signup-gender">Gender</label>
                  <select
                    id="patient-signup-gender"
                    className="auth_gateway_text_input"
                    value={patientSignup.gender}
                    onChange={(e) => updatePatientSignupField("gender", e.target.value)}
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Others">Other</option>
                  </select>
                </div>
                <div className="auth_gateway_field_row">
                  <div className="auth_gateway_field_wrap">
                    <label htmlFor="patient-signup-dob">Date of birth</label>
                    <input
                      id="patient-signup-dob"
                      type="date"
                      className="auth_gateway_text_input"
                      value={patientSignup.dob}
                      onChange={(e) => updatePatientSignupField("dob", e.target.value)}
                      max={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                  <div className="auth_gateway_field_wrap">
                    <label htmlFor="patient-signup-blood-group">Blood group</label>
                    <select
                      id="patient-signup-blood-group"
                      className="auth_gateway_text_input"
                      value={patientSignup.bloodGroup}
                      onChange={(e) => updatePatientSignupField("bloodGroup", e.target.value)}
                    >
                      <option value="">Select blood group</option>
                      <option value="A+">A+</option>
                      <option value="A-">A-</option>
                      <option value="B+">B+</option>
                      <option value="B-">B-</option>
                      <option value="AB+">AB+</option>
                      <option value="AB-">AB-</option>
                      <option value="O+">O+</option>
                      <option value="O-">O-</option>
                    </select>
                  </div>
                </div>
                <label className="auth_gateway_file_field">
                  <span>Profile image</span>
                  <div className="auth_gateway_image_uploader">
                    <div className="auth_gateway_image_uploader_preview">
                      {patientImagePreviewUrl ? (
                        <img src={patientImagePreviewUrl} alt="Profile preview" />
                      ) : (
                        <span className="auth_gateway_image_uploader_placeholder">No image selected</span>
                      )}
                    </div>
                    <div className="auth_gateway_image_uploader_actions">
                      <input
                        id="patient-signup-image"
                        type="file"
                        accept=".png,.jpg,.jpeg,.webp"
                        onChange={handlePatientImageChange}
                      />
                      <label htmlFor="patient-signup-image" className="auth_gateway_image_uploader_btn">
                        {patientSignup.imageFile ? "Change image" : "Choose image"}
                      </label>
                      <p className="auth_gateway_image_uploader_note">
                        JPG, PNG, WEBP | up to 5MB
                      </p>
                    </div>
                  </div>
                </label>
              </div>
              <label className="auth_gateway_policy">
                <input
                  type="checkbox"
                  checked={agreePolicy}
                  onChange={(event) =>
                    setAgreePolicyByContext((prev) => ({ ...prev, [mobileKey]: event.target.checked }))
                  }
                />
                <span>I agree to Terms and Privacy Policy.</span>
              </label>
            </>
          ) : null}

          <button type="submit" className="auth_gateway_submit_btn" disabled={isSubmitting}>
            {buttonLabel}
          </button>
          {isProviderSignup && role === "doctor" && providerStep === 6 ? (
            <button
              type="button"
              className="auth_gateway_back_step"
              onClick={() => {
                setInlineError("");
                void submitProviderSignup();
              }}
              disabled={isSubmitting}
            >
              Skip for now
            </button>
          ) : null}
          {isProviderSignup && providerStep > 1 ? (
            <button type="button" className="auth_gateway_back_step" onClick={goPreviousStep}>
              Back
            </button>
          ) : null}

          {inlineError ? <p className="auth_gateway_error">{inlineError}</p> : null}
          </form>
        </div>
      </div>
      {isProviderSignup && providerStep === 4 && isMobileStep4View && mobileSubcatModalCategoryId ? (
        <div className="auth_gateway_modal_backdrop" role="presentation" onClick={() => setMobileSubcatModalCategoryId("")}>
          <div
            className="auth_gateway_modal_card"
            role="dialog"
            aria-modal="true"
            aria-label="Related operations"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="auth_gateway_modal_head">
              <h3 title={categoryTitleById.get(mobileSubcatModalCategoryId) || "Category"}>
                {categoryTitleById.get(mobileSubcatModalCategoryId) || "Category"}
              </h3>
              <button type="button" className="auth_gateway_modal_close" onClick={() => setMobileSubcatModalCategoryId("")}>
                Close
              </button>
            </div>
            <div className="auth_gateway_modal_actions">
              <button
                type="button"
                className={`auth_gateway_toggle_action ${mobileModalSelectedCount > 0 ? "active" : ""}`}
                onClick={() => selectAllSubcategoriesForCategory(mobileSubcatModalCategoryId)}
              >
                <span className="auth_gateway_toggle_box" aria-hidden="true" />
                <span>Select all</span>
              </button>
              <button
                type="button"
                className={`auth_gateway_toggle_action ${
                  mobileModalItems.length > 0 && mobileModalSelectedCount === 0 ? "active" : ""
                }`}
                onClick={() => clearSubcategoriesForCategory(mobileSubcatModalCategoryId)}
              >
                <span className="auth_gateway_toggle_box" aria-hidden="true" />
                <span>All included</span>
              </button>
            </div>
            <div className="auth_gateway_modal_body">
              {subcategoryMap[mobileSubcatModalCategoryId] === undefined ? (
                <span className="auth_gateway_chip_hint">Loading operations…</span>
              ) : subcategoryMap[mobileSubcatModalCategoryId].length === 0 ? (
                <span className="auth_gateway_chip_hint">No operations listed yet.</span>
              ) : (
                <AuthGatewaySubcategoryTree
                  nodes={buildSubcategoryTree(subcategoryMap[mobileSubcatModalCategoryId])}
                  selectedIds={providerForm.subcategoryIds}
                  onToggle={(sid) => toggleSubcategory(sid)}
                />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
